import { createTourRegistry, type TourDefinition } from '@nucleus-commerce/admin-kit/tour';

import { auctionDetailTour, auctionsListTour } from './auctions';
import {
  attributesTour,
  collectionEditorTour,
  collectionNewTour,
  collectionsListTour,
  customizationsTour,
  mediaLibraryTour,
  productEditorBasicsTour,
  productEditorSellingTour,
  productEditorVariantsTour,
  productNewTour,
  productsTour,
  productTypesTour,
  sizeSetsTour,
  stonePricingTour,
  storeCatalogTour,
  storeProductTour,
} from './catalog';
import {
  blockEditorTour,
  blockNewTour,
  blocksTour,
  blogCategoriesTour,
  blogPostTour,
  blogTour,
  menuEditorTour,
  menusTour,
  pageEditorTour,
  pagesTour,
  redirectsTour,
  sectionEditorTour,
  sectionsTour,
  seoTour,
  sharedBlockEditorTour,
  sharedBlocksTour,
} from './content';
import {
  customerDetailTour,
  customersListTour,
  giftCardDetailTour,
  giftCardsListTour,
} from './customers';
import { apiKeysTour, webhookDetailTour, webhooksTour } from './developers';
import { discountEditorTour, discountsListTour } from './discounts';
import { helpCenterTour, homeOrgTour, homeStoreTour, welcomeTour } from './getting-started';
import { inventoryLevelsTour } from './inventory';
import {
  abandonedDetailTour,
  abandonedListTour,
  draftOrderDetailTour,
  draftOrdersListTour,
  orderActionsTour,
  orderDetailTour,
  ordersListTour,
  returnDetailTour,
  returnsListTour,
} from './orders';
import {
  productionBoardTour,
  productionCardTour,
  productionStageActionsTour,
  qcEditorTour,
  qcListTour,
  workflowEditorTour,
  workflowStageSettingsTour,
  workflowsListTour,
} from './production';
import {
  reportsAnalyticsTour,
  reportsCustomersTour,
  reportsInventoryTour,
  reportsOverviewTour,
  reportsPaymentsTour,
  reportsAuctionsTour,
  reportsProductsTour,
  reportsSalesTour,
} from './reports';
import { scheduleDetailTour, scheduleNewTour, schedulesListTour } from './schedules';
import {
  analyticsDestinationsTour,
  feedsTour,
  importsExportsTour,
  locationsTour,
  marketsTour,
  notificationsTour,
  paymentMethodsTour,
  policiesTour,
  policyEditorTour,
  recoveryTour,
  returnsSettingsTour,
  settingsHubTour,
  shippingTour,
  storeDetailsTour,
  taxTour,
} from './store-settings';
import {
  accountTour,
  activityTour,
  organizationSettingsTour,
  rolesTour,
  securityTour,
  storesTour,
  teamTour,
} from './team';

/** Help-centre groups, in the order the page shows them; copy at `tours:groups.<key>`. */
export const ADMIN_TOUR_GROUPS = [
  'getting-started',
  'catalog',
  'orders',
  'production',
  'customers',
  'marketing',
  'reports',
  'store-settings',
  'team',
  'developers',
] as const;

export type AdminTourGroup = (typeof ADMIN_TOUR_GROUPS)[number];

/** Every admin tour. Add a definition here and its copy to `locales/en/tours.json`. */
export const ADMIN_TOURS: readonly TourDefinition[] = [
  welcomeTour,
  homeOrgTour,
  homeStoreTour,
  helpCenterTour,
  productsTour,
  productEditorBasicsTour,
  productEditorVariantsTour,
  productEditorSellingTour,
  productNewTour,
  mediaLibraryTour,
  attributesTour,
  customizationsTour,
  productTypesTour,
  sizeSetsTour,
  stonePricingTour,
  storeCatalogTour,
  storeProductTour,
  collectionsListTour,
  collectionNewTour,
  collectionEditorTour,
  schedulesListTour,
  scheduleNewTour,
  scheduleDetailTour,
  inventoryLevelsTour,
  ordersListTour,
  orderDetailTour,
  orderActionsTour,
  draftOrdersListTour,
  draftOrderDetailTour,
  abandonedListTour,
  abandonedDetailTour,
  returnsListTour,
  returnDetailTour,
  auctionsListTour,
  auctionDetailTour,
  productionBoardTour,
  productionCardTour,
  productionStageActionsTour,
  workflowsListTour,
  workflowEditorTour,
  workflowStageSettingsTour,
  qcListTour,
  qcEditorTour,
  customersListTour,
  customerDetailTour,
  giftCardsListTour,
  giftCardDetailTour,
  discountsListTour,
  discountEditorTour,
  pagesTour,
  pageEditorTour,
  blogTour,
  blogPostTour,
  blogCategoriesTour,
  menusTour,
  menuEditorTour,
  blocksTour,
  blockNewTour,
  blockEditorTour,
  sectionsTour,
  sectionEditorTour,
  sharedBlocksTour,
  sharedBlockEditorTour,
  redirectsTour,
  seoTour,
  reportsOverviewTour,
  reportsSalesTour,
  reportsProductsTour,
  reportsCustomersTour,
  reportsPaymentsTour,
  reportsAuctionsTour,
  reportsInventoryTour,
  reportsAnalyticsTour,
  settingsHubTour,
  storeDetailsTour,
  marketsTour,
  paymentMethodsTour,
  shippingTour,
  taxTour,
  locationsTour,
  returnsSettingsTour,
  recoveryTour,
  notificationsTour,
  policiesTour,
  policyEditorTour,
  feedsTour,
  analyticsDestinationsTour,
  importsExportsTour,
  apiKeysTour,
  webhooksTour,
  webhookDetailTour,
  storesTour,
  teamTour,
  rolesTour,
  activityTour,
  organizationSettingsTour,
  accountTour,
  securityTour,
];

export const adminTourRegistry = createTourRegistry(ADMIN_TOURS);
